import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { getAccessToken } from 'lib/v2/auth';
import { getGladosNavUrl } from 'config';
import { setThemeMode } from 'store/v2/themes/actions';
import { logout } from 'store/v1/authentication/actions';
import compactLogo from 'assets/images/logo/logo-compact.svg';

const GladosNav = ({ profile }) => {
  const navRef = useRef(null);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    import('@signinsolutions/glados-shared-nav')
      .then(() => setLoaded(true))
      .catch((error) => {
        console.error('Failed to load glados-nav:', error); // eslint-disable-line
      });
  }, []);

  const userName = useMemo(() => {
    if (profile?.name) return profile.name;
    return `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim();
  }, [profile?.name, profile?.first_name, profile?.last_name]);

  const userInitials = useMemo(() => {
    return userName
      .split(' ')
      .map((part) => part[0])
      .filter(Boolean)
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, [userName]);

  const organisationName = profile?.company?.name || '';

  const handleNavClick = useCallback(
    (event) => {
      const { path, isExternal, ownerAppId } = event.detail;
      if (isExternal || ownerAppId !== 'sic') {
        window.location.href = path;
      } else {
        event.preventDefault();
        history.push(path);
      }
    },
    [history]
  );

  const handleThemeChange = useCallback(
    (event) => {
      dispatch(setThemeMode(event.detail.theme));
    },
    [dispatch]
  );

  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  useEffect(() => {
    const element = navRef.current;
    if (!element) return;

    element.addEventListener('nav-item-click', handleNavClick);
    element.addEventListener('theme-change', handleThemeChange);
    element.addEventListener('logout', handleLogout);

    return () => {
      element.removeEventListener('nav-item-click', handleNavClick);
      element.removeEventListener('theme-change', handleThemeChange);
      element.removeEventListener('logout', handleLogout);
    };
  }, [loaded, handleNavClick, handleThemeChange, handleLogout]);

  if (!loaded) return null;

  return (
    <glados-nav
      ref={navRef}
      style={{ all: 'initial', display: 'block' }}
      app-id="sic"
      auth-token={getAccessToken() || ''}
      api-base-url={getGladosNavUrl()}
      active-path={location.pathname}
      logo-url={compactLogo}
      user-email={profile?.email || ''}
      user-name={userName}
      user-initials={userInitials}
      organisation-name={organisationName}
    />
  );
};

const mapStateToProps = ({ profile }) => ({ profile });

export default connect(mapStateToProps)(GladosNav);
